import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useJobs } from '../hooks/useJobs';
import JobCard from '../components/JobCard';
import './Jobs.css';

const Jobs = () => {
  const navigate = useNavigate();
  const { location: locationParam, workerId } = useParams();
  const [searchParams] = useSearchParams();
  const workerName = searchParams.get('name');
  const { isAdmin } = useAuth();
  const { jobs, loading, editJob, removeJob } = useJobs();

  const handleStatusChange = async (id, status) => {
    await editJob(id, { status });
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    // Completed jobs go to the bottom for everyone
    const aCompleted = a.status === 'completed' ? 1 : 0;
    const bCompleted = b.status === 'completed' ? 1 : 0;
    if (aCompleted !== bCompleted) return aCompleted - bCompleted;

    if (isAdmin) {
      // Admin: newest created first
      const aDate = a.createdAt ? new Date(a.createdAt) : new Date(0);
      const bDate = b.createdAt ? new Date(b.createdAt) : new Date(0);
      return bDate - aDate;
    }
    // Fieldworker: ascending by scheduledDate (earliest first)
    const aDate = a.scheduledDate ? new Date(a.scheduledDate) : new Date(0);
    const bDate = b.scheduledDate ? new Date(b.scheduledDate) : new Date(0);
    return aDate - bDate;
  });

  // Apply URL filter (location or worker)
  const filteredJobs = sortedJobs.filter((job) => {
    if (locationParam) {
      const decoded = decodeURIComponent(locationParam);
      // Try to extract UK postcode from the location param
      const postcodeMatch = decoded.match(/[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}/i);
      if (postcodeMatch) {
        // Filter by postcode (partial match so any job at that postcode shows)
        const postcode = postcodeMatch[0];
        return job.location && job.location.toUpperCase().includes(postcode.toUpperCase());
      }
      // Fallback: exact match for full location string
      return job.location && job.location.toLowerCase() === decoded.toLowerCase();
    }
    if (workerId) {
      if (Array.isArray(job.assignedTo)) {
        return job.assignedTo.includes(workerId);
      }
      return job.assignedTo === workerId;
    }
    return true;
  });

  return (
    <div className="page jobs">
      <div className="jobs-header">
        <div>
          <div className="jobs-header-title-row">
            {(locationParam || workerId) && (
              <button className="back-btn" onClick={() => navigate('/jobs')}>
                ← Back
              </button>
            )}
            <h2>
              {locationParam
                ? `Jobs at ${(() => {
                    const decoded = decodeURIComponent(locationParam);
                    const m = decoded.match(/[A-Z]{1,2}\d[A-Z\d]?\s?\d[A-Z]{2}/i);
                    return m ? m[0].toUpperCase() : decoded;
                  })()}`
                : workerId
                ? `Jobs for ${workerName || 'Worker'}`
                : 'Jobs'}
            </h2>
          </div>
          <p className="filter-hint">
            {locationParam
              ? 'Showing all jobs at this location'
              : workerId
              ? 'Showing all jobs assigned to this worker'
              : ''}
          </p>
          {(locationParam || workerId) && (
            <button className="clear-filter-btn" onClick={() => navigate('/jobs')}>
              ✕ Clear Filter
            </button>
          )}
        </div>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => navigate('/jobs/new')}>
            New Job
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">Loading jobs...</div>
      ) : (
        <div className="jobs-list">
          {filteredJobs.map((job) => (
            <JobCard
              key={job.id}
              job={job}
              onStatusChange={handleStatusChange}
              onEdit={isAdmin ? (id) => editJob(id, job) : undefined}
              onDelete={isAdmin ? removeJob : undefined}
            />
          ))}
          {filteredJobs.length === 0 && <p>No jobs found.</p>}
        </div>
      )}
    </div>
  );
};

export default Jobs;
